import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth, API } from "@/App";
import { usePageContent } from "@/hooks/usePageContent";
import usePageTitle from "@/hooks/usePageTitle";
import axios from "axios";
import { 
  ArrowRight, 
  CheckCircle, 
  XCircle, 
  Lightbulb,
  ExternalLink,
  Newspaper,
  GraduationCap,
  Globe,
  BookOpen,
  Leaf,
  BarChart3,
  Info
} from "lucide-react";
import { toast } from "sonner";

const carbonQuiz = [
  { id: 1, question: "Qu'est-ce qu'un bilan carbone ?", context: "Fondamentaux", options: [{ text: "Un audit financier de l'entreprise", correct: false }, { text: "Une méthode de comptabilisation des émissions de GES", correct: true }, { text: "Une analyse des consommations d'électricité uniquement", correct: false }, { text: "Un rapport annuel obligatoire", correct: false }], explanation: "Le bilan carbone quantifie toutes les émissions de GES directes et indirectes d'une organisation." },
  { id: 2, question: "Quels sont les 3 scopes des émissions de GES ?", context: "Méthodologie", options: [{ text: "Passé, présent, futur", correct: false }, { text: "Scope 1 (directes), Scope 2 (énergie), Scope 3 (chaîne de valeur)", correct: true }, { text: "Air, eau, sol", correct: false }, { text: "Production, distribution, consommation", correct: false }], explanation: "Les scopes catégorisent les émissions selon leur origine dans l'organisation." },
  { id: 3, question: "Qu'est-ce que la méthode ACT ?", context: "Stratégie climat", options: [{ text: "Une méthode de tri des déchets", correct: false }, { text: "Un outil d'évaluation de l'alignement stratégique avec les objectifs climatiques", correct: true }, { text: "Un protocole de mesure atmosphérique", correct: false }, { text: "Une certification environnementale", correct: false }], explanation: "ACT évalue si la stratégie d'une entreprise est cohérente avec une trajectoire bas-carbone." },
  { id: 4, question: "Quel secteur représente la plus grande part du Scope 3 ?", context: "Enjeux sectoriels", options: [{ text: "Le chauffage des bureaux", correct: false }, { text: "Les déplacements des dirigeants", correct: false }, { text: "Les achats et la chaîne d'approvisionnement", correct: true }, { text: "L'éclairage des locaux", correct: false }], explanation: "Pour la plupart des entreprises, les achats représentent 70 à 90% de l'empreinte totale." },
  { id: 5, question: "Qu'est-ce que la neutralité carbone ?", context: "Objectifs climatiques", options: [{ text: "Ne plus émettre aucun CO2", correct: false }, { text: "Équilibrer les émissions résiduelles après avoir réduit au maximum", correct: true }, { text: "Acheter uniquement des crédits carbone", correct: false }, { text: "Utiliser uniquement de l'électricité renouvelable", correct: false }], explanation: "La neutralité implique d'abord de réduire drastiquement, puis de compenser le résidu." },
  { id: 6, question: "Quel est l'objectif de l'Accord de Paris ?", context: "Contexte réglementaire", options: [{ text: "Limiter le réchauffement à 3°C", correct: false }, { text: "Limiter le réchauffement bien en dessous de 2°C, idéalement 1,5°C", correct: true }, { text: "Atteindre 0 émission d'ici 2030", correct: false }, { text: "Doubler les forêts mondiales", correct: false }], explanation: "L'Accord de Paris vise à maintenir le réchauffement sous 2°C par rapport à l'ère préindustrielle." }
];

const mediaResources = [
  {
    name: "Bon Pote",
    icon: Globe,
    description: "Des articles et synthèses vulgarisées pour tout comprendre aux enjeux climatiques et démonter les idées reçues.",
    url: "https://bonpote.com/",
    color: "var(--maquis)"
  },
  {
    name: "Vert, le média",
    icon: Newspaper,
    description: "Le journal quotidien indépendant qui décrypte l'actualité de la transition écologique de façon claire et accessible.",
    url: "https://vert.eco/",
    color: "var(--terracotta)"
  },
  {
    name: "Reporterre",
    icon: Leaf,
    description: "Le quotidien de l'écologie, un média indépendant en accès libre.",
    url: "https://reporterre.net/",
    color: "var(--maquis-dark)"
  }
];

const eduResources = [
  {
    name: "M'ta Terre",
    icon: BookOpen,
    description: "La plateforme de l'ADEME dédiée aux jeunes, lycéens et étudiants pour comprendre et agir pour la transition écologique.",
    url: "https://mtaterre.fr/",
    color: "var(--terracotta)"
  },
  {
    name: "ADEME — Acteurs de l'éducation",
    icon: GraduationCap,
    description: "Un catalogue complet de 150 ressources pédagogiques, kits et guides gratuits pour les enseignants, formateurs et équipes éducatives.",
    url: "https://agirpourlatransition.ademe.fr/acteurs-education",
    color: "var(--maquis)"
  },
  {
    name: "Réseau Action Climat",
    icon: BarChart3,
    description: "Des guides synthétiques et pédagogiques pour décrypter le climat et l'alimentation durable.",
    url: "https://reseauactionclimat.org/",
    color: "var(--terracotta-dark)"
  }
];

const ResourceCard = ({ resource }) => (
  <div className="card-premium p-6 md:p-8 flex flex-col h-full" data-testid={`resource-${resource.name.toLowerCase().replace(/\s+/g, '-')}`}>
    <div className="flex items-start gap-4 mb-4">
      <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${resource.color}15` }}>
        <resource.icon className="w-6 h-6" style={{ color: resource.color }} />
      </div>
      <h3 className="font-display text-xl font-bold text-[var(--charcoal)] mt-1">{resource.name}</h3>
    </div>
    <p className="text-[var(--stone)] leading-relaxed mb-6 flex-1">{resource.description}</p>
    <a
      href={resource.url}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-2 px-5 py-3 rounded-full text-sm font-medium transition-colors self-start"
      style={{ backgroundColor: resource.color, color: 'white' }}
    >
      Consulter
      <ExternalLink className="w-4 h-4" />
    </a>
  </div>
);

const StrategieClimat = () => {
  const navigate = useNavigate();
  const { isAuthenticated, token } = useAuth();
  const { getField } = usePageContent("climat");
  usePageTitle("Ressources Utiles");
  const [quizStarted, setQuizStarted] = useState(false);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [showResults, setShowResults] = useState(false);
  const [score, setScore] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [showExplanation, setShowExplanation] = useState(false);

  const handleStartQuiz = () => {
    setQuizStarted(true);
    setCurrentQuestion(0);
    setAnswers({});
    setShowResults(false);
    setScore(0);
    setSelectedAnswer(null);
    setShowExplanation(false);
  };

  const handleAnswer = (option, index) => {
    if (selectedAnswer !== null) return;
    setSelectedAnswer(index);
    setShowExplanation(true);
    const newAnswers = { ...answers, [currentQuestion]: { ...option, questionId: carbonQuiz[currentQuestion].id } };
    setAnswers(newAnswers);
    if (option.correct) setScore(prev => prev + 1);
  };

  const handleNext = () => {
    if (currentQuestion < carbonQuiz.length - 1) {
      setCurrentQuestion(prev => prev + 1);
      setSelectedAnswer(null);
      setShowExplanation(false);
    } else {
      setShowResults(true);
      if (isAuthenticated) saveQuizResult();
    }
  };

  const saveQuizResult = async () => {
    try {
      await axios.post(`${API}/quiz/result`, {
        quiz_type: "carbon_footprint",
        score: score + (selectedAnswer !== null && carbonQuiz[currentQuestion].options[selectedAnswer].correct ? 1 : 0),
        max_score: carbonQuiz.length,
        answers
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Résultats sauvegardés !");
    } catch (error) {
      console.error("Failed to save results:", error);
    }
  };

  const progress = ((currentQuestion + 1) / carbonQuiz.length) * 100;
  const question = carbonQuiz[currentQuestion];
  const percentage = Math.round((score / carbonQuiz.length) * 100);

  return (
    <div className="min-h-screen bg-[var(--bone)]" data-testid="strategie-climat-page">
      {/* Hero Section */}
      <section className="pt-32 pb-20 relative overflow-hidden">
        <div className="blob-bg w-[500px] h-[500px] bg-[var(--terracotta)]/15 top-0 right-0"></div>
        <div className="blob-bg w-[300px] h-[300px] bg-[var(--maquis)]/10 bottom-0 left-0"></div>

        <div className="container-premium relative z-10">
          <div className="max-w-3xl">
            <h1 className="font-display text-5xl md:text-6xl font-bold text-[var(--charcoal)] mb-6 leading-tight">
              Ressources <span className="text-[var(--terracotta)] emphasis">utiles</span>
            </h1>
            <p className="text-xl text-[var(--stone)] mb-10 leading-relaxed">
              S'informer, comprendre et transmettre sans influence.
            </p>
          </div>
        </div>
      </section>

      {/* Resources - visible when quiz is not active */}
      {!quizStarted && !showResults && (
        <>
          {/* Introduction */}
          <section className="pb-12" data-testid="resources-intro">
            <div className="container-premium">
              <div className="max-w-4xl mx-auto">
                <p className="text-lg text-[var(--stone)] leading-relaxed">
                  Parce qu'il est essentiel d'avoir accès à une information scientifique rigoureuse et indépendante pour construire son esprit critique et alimenter ses réflexions à tout âge, je vous partage ici une sélection de ressources de référence* :
                </p>
              </div>
            </div>
          </section>

          {/* Médias indépendants */}
          <section className="pb-20" data-testid="media-resources">
            <div className="container-premium">
              <div className="max-w-4xl mx-auto">
                <div className="flex items-center gap-3 mb-8">
                  <Newspaper className="w-6 h-6 text-[var(--terracotta)]" />
                  <h2 className="font-display text-2xl md:text-3xl font-bold text-[var(--charcoal)]">
                    Médias indépendants & Décryptage
                  </h2>
                </div>
                <div className="grid md:grid-cols-3 gap-6">
                  {mediaResources.map((resource, idx) => (
                    <ResourceCard key={idx} resource={resource} />
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* Plateformes pédagogiques */}
          <section className="pb-20 bg-[var(--sand)]" data-testid="edu-resources">
            <div className="container-premium py-20">
              <div className="max-w-4xl mx-auto">
                <div className="flex items-center gap-3 mb-8">
                  <GraduationCap className="w-6 h-6 text-[var(--maquis)]" />
                  <h2 className="font-display text-2xl md:text-3xl font-bold text-[var(--charcoal)]">
                    Plateformes pédagogiques & Jeunesse
                  </h2>
                </div>
                <div className="grid md:grid-cols-3 gap-6">
                  {eduResources.map((resource, idx) => (
                    <ResourceCard key={idx} resource={resource} />
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* Disclaimer */}
          <section className="pb-20" data-testid="disclaimer-section">
            <div className="container-premium">
              <div className="max-w-4xl mx-auto">
                <div className="card-premium p-6 md:p-8 border-l-4 border-[var(--stone)]/30">
                  <div className="flex items-start gap-4">
                    <Info className="w-5 h-5 text-[var(--stone)] mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-[var(--stone)] leading-relaxed italic">
                      * Aucun partenariat n'a été mis en place avec ces ressources mais je les consulte régulièrement et elles me permettent de m'informer, donc je trouvais intéressant de les partager.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Quiz teaser - accessible from Domaines d'Intervention link */}
          <section className="pb-20" data-testid="quiz-teaser">
            <div className="container-premium">
              <div className="max-w-4xl mx-auto">
                <div className="card-premium p-8 md:p-10 text-center bg-[var(--sand)]">
                  <h3 className="font-display text-2xl font-bold text-[var(--charcoal)] mb-4">
                    Testez vos connaissances climat
                  </h3>
                  <p className="text-[var(--stone)] mb-6 max-w-xl mx-auto">
                    Bilan carbone, scopes d'émissions, Accord de Paris... Évaluez votre compréhension des enjeux climatiques en 6 questions.
                  </p>
                  <button
                    onClick={handleStartQuiz}
                    className="btn-primary inline-flex items-center gap-2"
                    data-testid="start-carbon-quiz-btn"
                  >
                    Tester mes connaissances
                    <ArrowRight className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
          </section>

          {/* CTA */}
          <section className="section-premium relative overflow-hidden">
            <div className="blob-bg w-[500px] h-[500px] bg-[var(--terracotta)]/20 top-0 right-0"></div>
            <div className="container-premium relative z-10 text-center">
              <h2 className="font-display text-4xl md:text-5xl font-bold text-[var(--charcoal)] mb-8">
                Prêt à transmettre<br />
                <span className="text-[var(--terracotta)] emphasis">ensemble</span> ?
              </h2>
              <p className="text-[var(--stone)] text-xl max-w-2xl mx-auto mb-12">
                Faisons le point ensemble sur vos enjeux et construisons un parcours adapté à votre contexte et vos attentes.
              </p>
              <Link to="/contact">
                <button className="btn-primary text-lg px-10 py-5">
                  Prendre rendez-vous
                </button>
              </Link>
            </div>
          </section>
        </>
      )}

      {/* Quiz Section */}
      {quizStarted && !showResults && (
        <section className="pb-20">
          <div className="container-premium">
            <div className="max-w-3xl mx-auto">
              <div className="glass-warm rounded-2xl p-6 mb-6">
                <div className="flex justify-between items-center mb-3">
                  <span className="text-sm font-medium text-[var(--stone)]">Question {currentQuestion + 1} / {carbonQuiz.length}</span>
                  <span className="text-sm font-medium text-[var(--terracotta)]">Score: {score}</span>
                </div>
                <div className="h-2 bg-[var(--sand)] rounded-full overflow-hidden">
                  <div className="h-full bg-[var(--terracotta)] rounded-full transition-all duration-500" style={{ width: `${progress}%` }} />
                </div>
              </div>

              <div className="card-premium overflow-hidden" data-testid="carbon-quiz-card">
                <div className="p-8 md:p-10">
                  <div className="mb-8">
                    <span className="text-xs uppercase tracking-[0.2em] font-bold text-[var(--terracotta)]">{question.context}</span>
                    <h3 className="font-display text-2xl font-semibold text-[var(--charcoal)] mt-2">{question.question}</h3>
                  </div>

                  <div className="space-y-3">
                    {question.options.map((option, index) => (
                      <button
                        key={index}
                        onClick={() => handleAnswer(option, index)}
                        disabled={selectedAnswer !== null}
                        data-testid={`carbon-option-${index}`}
                        className={`w-full text-left quiz-option-premium flex items-center gap-4 ${
                          selectedAnswer === index 
                            ? option.correct ? 'correct' : 'incorrect'
                            : selectedAnswer !== null && option.correct ? 'correct' : ''
                        }`}
                      >
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                          selectedAnswer !== null
                            ? option.correct ? 'bg-[var(--maquis)]/10 text-[var(--maquis)]' : selectedAnswer === index ? 'bg-[var(--terracotta)]/10 text-[var(--terracotta)]' : 'bg-[var(--sand)]'
                            : 'bg-[var(--sand)]'
                        }`}>
                          {selectedAnswer !== null ? (
                            option.correct ? <CheckCircle className="w-5 h-5" /> : 
                            selectedAnswer === index ? <XCircle className="w-5 h-5" /> : 
                            <span className="text-sm font-medium">{String.fromCharCode(65 + index)}</span>
                          ) : (
                            <span className="text-sm font-medium text-[var(--stone)]">{String.fromCharCode(65 + index)}</span>
                          )}
                        </div>
                        <span className="flex-1 text-[var(--charcoal)]">{option.text}</span>
                      </button>
                    ))}
                  </div>

                  {showExplanation && (
                    <div className="mt-8 p-6 bg-[var(--sand)] rounded-2xl animate-fade-in">
                      <div className="flex items-start gap-3">
                        <Lightbulb className="w-5 h-5 text-[var(--terracotta)] mt-0.5 flex-shrink-0" />
                        <div>
                          <span className="font-semibold text-[var(--charcoal)]">Explication : </span>
                          <span className="text-[var(--stone)]">{question.explanation}</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {selectedAnswer !== null && (
                  <div className="px-8 md:px-10 pb-8 md:pb-10">
                    <button onClick={handleNext} className="btn-primary w-full flex items-center justify-center gap-2" data-testid="carbon-next-btn">
                      {currentQuestion < carbonQuiz.length - 1 ? "Question suivante" : "Voir les résultats"}
                      <ArrowRight className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Results */}
      {showResults && (
        <section className="pb-20">
          <div className="container-premium">
            <div className="max-w-2xl mx-auto card-premium overflow-hidden" data-testid="carbon-results">
              <div className={`p-10 text-center ${percentage >= 70 ? 'bg-[var(--maquis)]/10' : 'bg-[var(--terracotta)]/10'}`}>
                <div className="w-24 h-24 mx-auto rounded-full flex items-center justify-center mb-6 bg-[var(--bone)]">
                  <Globe className={`w-12 h-12 ${percentage >= 70 ? 'text-[var(--maquis)]' : 'text-[var(--terracotta)]'}`} />
                </div>
                <h2 className="font-display text-3xl font-bold text-[var(--charcoal)] mb-2">
                  {percentage >= 70 ? "Expert climat !" : percentage >= 40 ? "En bonne voie !" : "Continuez d'apprendre !"}
                </h2>
                <p className="text-[var(--stone)]">Vous avez obtenu {score} / {carbonQuiz.length} ({percentage}%)</p>
              </div>
              <div className="p-10">
                <p className="text-[var(--stone)] text-center mb-8">
                  Continuez à approfondir vos connaissances avec les ressources indépendantes ci-dessus.
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <button onClick={handleStartQuiz} className="btn-secondary flex-1">Refaire le quiz</button>
                  <button onClick={() => navigate('/contact')} className="btn-primary flex-1 flex items-center justify-center gap-2">
                    Me faire accompagner <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
                {!isAuthenticated && (
                  <p className="text-center text-sm text-[var(--stone)] mt-6">
                    <a href="/auth" className="text-[var(--terracotta)] font-medium hover:underline">Créez votre espace</a> pour sauvegarder vos résultats
                  </p>
                )}
              </div>
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default StrategieClimat;
